import Phaser from 'phaser';
import { setupCamera } from '../dpr.js';
import { TOME_DEFINITIONS, TOME_IDS } from '@roe/shared';
import { CONSUMABLE_DEFS } from '@roe/shared';
import { TROPHY_POOL, RELIC_NAME_ICON_MAP } from '@roe/shared';

interface RelicEntry { name: string; effect: string; tier: 'normal' | 'unique'; icon: string; }

const RELICS: RelicEntry[] = [
  // Offensive
  { name: 'Warblade Sigil',        effect: 'Auto attacks deal +15% bonus damage',                    tier: 'normal', icon: '⚔️' },
  { name: 'Orb of Arcane Fury',    effect: 'Spell damage increased by 12%',                          tier: 'normal', icon: '🔮' },
  { name: 'Emblem of Precision',   effect: 'Critical strikes deal +25% extra damage',                tier: 'normal', icon: '🎯' },
  { name: 'Idol of Swiftness',     effect: '+8% attack speed',                                       tier: 'normal', icon: '⚡' },
  { name: 'Talisman of the Fifth', effect: 'Every 5th attack deals double damage',                   tier: 'normal', icon: '🖐️' },
  { name: 'Charm of Twin Strikes', effect: 'Attacks have 10% chance to strike twice',                tier: 'normal', icon: '✌️' },
  { name: 'Totem of Bloodlust',    effect: 'Kills grant +20% damage for 5s',                         tier: 'normal', icon: '🩸' },
  // Defensive
  { name: 'Aegis of Stoneskin',    effect: 'Reduce incoming damage by 8%',                           tier: 'normal', icon: '🛡️' },
  { name: 'Ward of the Guardian',  effect: 'Taking damage has 15% chance to shield for 60 HP',       tier: 'normal', icon: '💠' },
  { name: 'Chalice of Renewal',    effect: '+2 HP regen per second in combat',                       tier: 'normal', icon: '🏺' },
  { name: 'Bulwark Emblem',        effect: 'Block 15% of damage when above 50% HP',                  tier: 'normal', icon: '🧱' },
  { name: 'Sigil of Vampirism',    effect: 'On hit: 10% chance to heal for 5% max HP',               tier: 'normal', icon: '🧛' },
  // Utility
  { name: 'Goldseer Idol',         effect: '+20% gold from kills',                                   tier: 'normal', icon: '🪙' },
  { name: 'Book of Enlightenment', effect: '+15% XP from all sources',                               tier: 'normal', icon: '📖' },
  { name: 'Scavenger Charm',       effect: 'Enemies drop +10% more loot',                            tier: 'normal', icon: '🧲' },
  { name: 'Fortunekeeper Orb',     effect: '+10% loot drop chance',                                  tier: 'normal', icon: '🎱' },
  { name: 'Mindstone Talisman',    effect: 'Abilities cost 10% less resource',                       tier: 'normal', icon: '🧿' },
  { name: 'Chronoshard',           effect: 'Ability cooldowns reduced by 8%',                        tier: 'normal', icon: '⏳' },
  { name: 'Soulspark Sigil',       effect: 'Critical strikes restore 5 resource',                    tier: 'normal', icon: '✨' },
  { name: 'Manafount Charm',       effect: 'Gain 3 mana on each auto-attack',                        tier: 'normal', icon: '💧' },
  { name: 'Frostgrasp Totem',      effect: 'Nearby enemies move 10% slower',                         tier: 'normal', icon: '❄️' },
  { name: 'Slayer Emblem',         effect: '+25% attack speed for 5s after killing an enemy',        tier: 'normal', icon: '💀' },
  { name: 'Vanguard Ward',         effect: 'Start each wave with a shield equal to 12% of max HP',   tier: 'normal', icon: '🏰' },
  { name: 'Crescendo Idol',        effect: 'Gain 1% damage for each wave cleared (accumulates)',      tier: 'normal', icon: '📈' },
  { name: "Archon's Focus",        effect: '+1% SP per kill. Each drop rolls its max (rare 30-50, epic 50-75, leg 75-99). Resets on death.', tier: 'normal', icon: '✧' },
  { name: "Spellbinder's Ring",    effect: 'Spells have 10% chance to cast twice (cap 25%)',           tier: 'normal', icon: '✦' },
  // Unique
  { name: 'Resonant Prong',        effect: 'Critical hits deal their damage a second time at 50%.',  tier: 'unique', icon: '🔱' },
  { name: 'Bloodtally Knife',      effect: '5% on kill: +1% ATK permanently (up to +100%/run).',    tier: 'unique', icon: '🗡️' },
  { name: 'Timestop Pendant',      effect: 'Once per 90s: prevents lethal hit + freeze 4s.',         tier: 'unique', icon: '⏱️' },
  { name: 'Storm Capacitor',       effect: '25% on auto: arc lightning for 80% ATK to rnd enemy.',   tier: 'unique', icon: '🌩️' },
  { name: 'Overflow Crystal',      effect: 'Doubles max resource. Regen +80%.',                      tier: 'unique', icon: '◆' },
  { name: "Forgemaster's Ingot",   effect: 'All weapons grant +40% of their stat bonuses.',          tier: 'unique', icon: '🔨' },
  { name: 'Spellfire Lens',        effect: 'Spell DMG × (1 + SP/200). At 200 SP = double spells.',  tier: 'unique', icon: '🔭' },
  { name: 'Echo Catalyst',         effect: '25% after spell: echo it for 50% SP arcane damage.',    tier: 'unique', icon: '🔄' },
  { name: 'Radiant Surge',         effect: 'After healer heals, target gains +80% DMG for 3s.',     tier: 'unique', icon: '☀️' },
  { name: 'Mana Flux Crystal',     effect: 'Full resource → next ability +80% DMG, drains all.',    tier: 'unique', icon: '🌀' },
];

type TabId = 'tomes' | 'relics' | 'consumables' | 'mimic';

export class CodexScene extends Phaser.Scene {
  private scrollContainer!: Phaser.GameObjects.Container;
  private scrollY = 0;
  private maxScroll = 0;
  private scrollBarThumb: Phaser.GameObjects.Rectangle | null = null;
  private activeTab: TabId = 'tomes';
  static lastTab: TabId = 'tomes';

  constructor() { super({ key: 'CodexScene' }); }

  create(): void {
    setupCamera(this, this.cameras.main, 'full');
    this.activeTab = CodexScene.lastTab;
    this.scrollY = 0;
    this.maxScroll = 0;
    this.scrollBarThumb = null;

    const W = 960;
    const H = 576;
    const cx = W / 2;
    this.cameras.main.setBackgroundColor('#0a0608');

    const canvas = this.game.canvas;
    canvas.setAttribute('tabindex', '1');
    canvas.focus();
    if (this.input.keyboard) {
      this.input.keyboard.once('keydown-ESC', () => this.scene.start('MenuScene'));
    }
    this.input.on('pointerdown', () => canvas.focus());

    this.drawLayout(cx, W, H);
  }

  private drawLayout(cx: number, W: number, H: number): void {
    const HEADER_H = 82;

    // ── Fixed header ──
    this.add.rectangle(cx, HEADER_H / 2, W, HEADER_H, 0x0a0806, 1).setDepth(10);
    this.add.rectangle(cx, HEADER_H, W, 1, 0x5a3600, 0.9).setDepth(10);

    this.add.text(cx, 18, '✦  Codex  ✦', {
      fontSize: '18px', fontFamily: 'Georgia, serif', color: '#c8973a', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(11);

    this.add.text(cx, 40, 'A reference guide to all Books, Relics and Consumables', {
      fontSize: '9px', fontFamily: 'Georgia, serif', color: '#5a3600',
    }).setOrigin(0.5).setDepth(11);

    // Close button
    const closeBtn = this.add.text(W - 24, 18, '✕', {
      fontSize: '16px', fontFamily: 'Georgia, serif', color: '#5a3600',
    }).setOrigin(0.5).setInteractive({ useHandCursor: true }).setDepth(11);
    closeBtn.on('pointerover', () => closeBtn.setColor('#c8973a'));
    closeBtn.on('pointerout', () => closeBtn.setColor('#5a3600'));
    closeBtn.on('pointerdown', () => this.scene.start('MenuScene'));

    // ── Tabs (no emoji icons) ──
    const tabDefs: { id: TabId; label: string }[] = [
      { id: 'tomes',       label: 'Books' },
      { id: 'relics',      label: 'Relics' },
      { id: 'consumables', label: 'Consumables' },
      { id: 'mimic',       label: 'Mimic' },
    ];
    const tabW = 116;
    const tabGap = 8;
    const tabsTotalW = tabDefs.length * tabW + (tabDefs.length - 1) * tabGap;
    const tabStartX = (W - tabsTotalW) / 2 + tabW / 2;

    for (let ti = 0; ti < tabDefs.length; ti++) {
      const tab = tabDefs[ti];
      const tx = tabStartX + ti * (tabW + tabGap);
      const isActive = this.activeTab === tab.id;
      const tabBg = this.add.rectangle(tx, 65, tabW, 22,
        isActive ? 0x1a1208 : 0x0d0a08, 1)
        .setStrokeStyle(1, isActive ? 0xc8973a : 0x5a3600).setDepth(11);
      const tabTxt = this.add.text(tx, 65, tab.label, {
        fontSize: '10px', fontFamily: 'Georgia, serif',
        color: isActive ? '#c8973a' : '#5a3600', fontStyle: isActive ? 'bold' : 'normal',
      }).setOrigin(0.5).setDepth(12).setInteractive({ useHandCursor: true });
      tabBg.setInteractive({ useHandCursor: true });
      const switchTab = () => {
        if (this.activeTab !== tab.id) {
          CodexScene.lastTab = tab.id;
          this.scene.restart();
        }
      };
      tabBg.on('pointerdown', switchTab);
      tabTxt.on('pointerdown', switchTab);
      if (!isActive) {
        tabBg.on('pointerover', () => { tabTxt.setColor('#a07840'); tabBg.setStrokeStyle(1, 0x9a6020); });
        tabBg.on('pointerout', () => { tabTxt.setColor('#5a3600'); tabBg.setStrokeStyle(1, 0x5a3600); });
      }
    }

    const SCROLL_AREA_H = H - HEADER_H;
    this.scrollContainer = this.add.container(0, HEADER_H);

    if (this.activeTab === 'tomes') {
      this.buildTomesContent(W);
    } else if (this.activeTab === 'relics') {
      this.buildRelicsContent(W);
    } else if (this.activeTab === 'consumables') {
      this.buildConsumablesContent(W);
    } else {
      this.buildMimicContent(W);
    }

    // Clip mask
    const maskShape = this.make.graphics({});
    maskShape.fillRect(0, HEADER_H, W, SCROLL_AREA_H);
    this.scrollContainer.setMask(maskShape.createGeometryMask());

    // Scroll bar
    if (this.maxScroll > 0) {
      const trackX = W - 6;
      this.add.rectangle(trackX, HEADER_H + SCROLL_AREA_H / 2, 4, SCROLL_AREA_H, 0x3a2800, 0.5).setDepth(12);
      const thumbH = Math.max(20, (SCROLL_AREA_H / (SCROLL_AREA_H + this.maxScroll)) * SCROLL_AREA_H);
      this.scrollBarThumb = this.add.rectangle(trackX, HEADER_H, 4, thumbH, 0x5a3600, 0.8)
        .setOrigin(0.5, 0).setDepth(12);
    }

    this.input.on('wheel', (_p: any, _o: any, _dx: number, deltaY: number) => {
      this.doScroll(deltaY * 0.5, HEADER_H, SCROLL_AREA_H);
    });
  }

  // ── Tomes: 3 per row, no description, keep Max 99 ──
  private buildTomesContent(W: number): void {
    const cols = 3;
    const PAD = 10;
    const GAP = 8;
    const cardW = Math.floor((W - PAD * 2 - GAP * (cols - 1)) / cols);
    const CARD_H = 52;
    let y = 10;

    for (let i = 0; i < TOME_IDS.length; i++) {
      const id = TOME_IDS[i];
      const def = TOME_DEFINITIONS[id];
      const col = i % cols;
      const row = Math.floor(i / cols);
      if (col === 0) y = 10 + row * (CARD_H + 6);

      const cx = PAD + col * (cardW + GAP) + cardW / 2;
      const cy = y + CARD_H / 2;
      const tomeColor = Phaser.Display.Color.HexStringToColor(def.color).color;

      const bg = this.add.rectangle(cx, cy, cardW, CARD_H - 2, 0x0d0a08, 0.95)
        .setStrokeStyle(1, tomeColor, 0.6);
      this.scrollContainer.add(bg);

      // Icon — padding prevents emoji from being clipped at text bounds
      const iconX = PAD + col * (cardW + GAP) + 14;
      const icon = this.add.text(iconX, y + 6, def.icon, {
        fontSize: '18px', padding: { x: 4, y: 4 },
      });
      this.scrollContainer.add(icon);

      // Name
      const name = this.add.text(iconX + 28, y + 7, def.name, {
        fontSize: '11px', fontFamily: 'Georgia, serif', color: def.color, fontStyle: 'bold',
      });
      this.scrollContainer.add(name);

      // Description — full `def.description` text so tomes with multi-effect wording
      // (e.g. Book of the Archivist's "+2 SP AND +0.2% resist to random school") read
      // accurately. Previously only the bonusPerLevel primary stat was shown, hiding
      // the secondary effect from the codex. `Max N` appended inline for scaling.
      const descLine = `${def.description}  ·  Max ${def.maxLevel}`;
      const bonus = this.add.text(iconX + 28, y + 24, descLine, {
        fontSize: '9px', fontFamily: 'Georgia, serif', color: '#a07840',
        wordWrap: { width: cardW - 50 }, lineSpacing: 1,
      });
      this.scrollContainer.add(bonus);
    }

    const rows = Math.ceil(TOME_IDS.length / cols);
    const contentH = rows * (CARD_H + 6) + 10;
    const SCROLL_AREA_H = 576 - 82;
    this.maxScroll = Math.max(0, contentH - SCROLL_AREA_H);
  }

  // ── Relics: 3 per row with icons ──
  private buildRelicsContent(W: number): void {
    const cols = 3;
    const PAD = 10;
    const GAP = 8;
    const cardW = Math.floor((W - PAD * 2 - GAP * (cols - 1)) / cols);
    const CARD_H = 58;
    let y = 10;

    const normalRelics = RELICS.filter((r) => r.tier === 'normal');
    const uniqueRelics = RELICS.filter((r) => r.tier === 'unique');

    const addHeader = (text: string, color: string): void => {
      const hdr = this.add.text(W / 2, y + 6, text, {
        fontSize: '11px', fontFamily: 'Georgia, serif', color, fontStyle: 'bold',
      }).setOrigin(0.5);
      this.scrollContainer.add(hdr);
      y += 22;
    };

    const addRow = (rowRelics: RelicEntry[], baseY: number): void => {
      for (let i = 0; i < rowRelics.length; i++) {
        const r = rowRelics[i];
        const col = i % cols;
        const cy = baseY + CARD_H / 2;
        const cx = PAD + col * (cardW + GAP) + cardW / 2;
        const borderColor = r.tier === 'unique' ? 0xc8973a : 0x5a3600;

        const bg = this.add.rectangle(cx, cy, cardW, CARD_H - 4, 0x0d0a08, 0.95)
          .setStrokeStyle(1, borderColor, r.tier === 'unique' ? 0.8 : 0.5);
        this.scrollContainer.add(bg);

        // Icon — use in-game sprite if available, fall back to emoji
        const iconX = PAD + col * (cardW + GAP) + 13;
        const spriteKey = RELIC_NAME_ICON_MAP[r.name];
        if (spriteKey && this.textures.exists(spriteKey)) {
          const iconImg = this.add.image(iconX + 8, baseY + (CARD_H - 4) / 2, spriteKey).setDisplaySize(22, 22);
          this.scrollContainer.add(iconImg);
        } else {
          this.scrollContainer.add(this.add.text(iconX, baseY + 6, r.icon, {
            fontSize: '16px', padding: { x: 4, y: 4 },
          }));
        }

        // Name + optional UNIQUE badge
        const nameColor = r.tier === 'unique' ? '#c8973a' : '#a07840';
        const nameTxt = this.add.text(iconX + 24, baseY + 8, r.name, {
          fontSize: '10px', fontFamily: 'Georgia, serif', color: nameColor, fontStyle: 'bold',
        });
        this.scrollContainer.add(nameTxt);

        if (r.tier === 'unique') {
          const badge = this.add.text(iconX + 24 + nameTxt.width + 5, baseY + 10, 'UNIQUE', {
            fontSize: '9px', fontFamily: 'Georgia, serif', color: '#c8973a',
            backgroundColor: '#1a1208', padding: { x: 2, y: 1 },
          });
          this.scrollContainer.add(badge);
        }

        const effectTxt = this.add.text(iconX + 24, baseY + 26, r.effect, {
          fontSize: '9px', fontFamily: 'Georgia, serif', color: '#6b4020',
          wordWrap: { width: cardW - 40 },
        });
        this.scrollContainer.add(effectTxt);
      }
    };

    addHeader('✦  Common Relics  ✦', '#a07840');
    for (let row = 0; row < Math.ceil(normalRelics.length / cols); row++) {
      const rowRelics = normalRelics.slice(row * cols, row * cols + cols);
      addRow(rowRelics, y);
      y += CARD_H;
    }

    y += 8;
    addHeader('✦  Unique Relics  ✦', '#c8973a');
    for (let row = 0; row < Math.ceil(uniqueRelics.length / cols); row++) {
      const rowRelics = uniqueRelics.slice(row * cols, row * cols + cols);
      addRow(rowRelics, y);
      y += CARD_H;
    }

    const SCROLL_AREA_H = 576 - 82;
    this.maxScroll = Math.max(0, y + 8 - SCROLL_AREA_H);
  }

  // ── Consumables tab ──
  private buildConsumablesContent(W: number): void {
    const cols = 3;
    const PAD = 10;
    const GAP = 8;
    const cardW = Math.floor((W - PAD * 2 - GAP * (cols - 1)) / cols);
    const CARD_H = 80;
    let y = 10;

    const hdr = this.add.text(W / 2, y + 6, '✦  Consumables  ✦', {
      fontSize: '11px', fontFamily: 'Georgia, serif', color: '#a07840', fontStyle: 'bold',
    }).setOrigin(0.5);
    this.scrollContainer.add(hdr);
    y += 22;

    const rarityColors: Record<string, string> = {
      common: '#9ca3af', uncommon: '#22c55e', rare: '#3b82f6',
      epic: '#a855f7', legendary: '#f97316', unique: '#cc3322',
    };

    for (let i = 0; i < CONSUMABLE_DEFS.length; i++) {
      const c = CONSUMABLE_DEFS[i];
      const col = i % cols;
      const row = Math.floor(i / cols);
      if (col === 0) y = 32 + row * (CARD_H + 6);

      const cx = PAD + col * (cardW + GAP) + cardW / 2;
      const cy = y + CARD_H / 2;
      const col_color = Phaser.Display.Color.HexStringToColor(rarityColors[c.rarity] ?? '#9ca3af').color;

      const bg = this.add.rectangle(cx, cy, cardW, CARD_H - 4, 0x0d0a08, 0.95)
        .setStrokeStyle(1, col_color, 0.7);
      this.scrollContainer.add(bg);

      const iconX = PAD + col * (cardW + GAP) + 13;
      if (c.spriteKey && this.textures.exists(c.spriteKey)) {
        const img = this.add.image(iconX + 10, y + 18, c.spriteKey).setDisplaySize(28, 28);
        this.scrollContainer.add(img);
      } else {
        this.scrollContainer.add(this.add.text(iconX, y + 4, c.icon, {
          fontSize: '20px', padding: { x: 4, y: 4 },
        }));
      }

      this.scrollContainer.add(this.add.text(iconX + 28, y + 7, c.name, {
        fontSize: '10px', fontFamily: 'Georgia, serif',
        color: rarityColors[c.rarity] ?? '#9ca3af', fontStyle: 'bold',
      }));

      this.scrollContainer.add(this.add.text(iconX + 28, y + 23, c.description, {
        fontSize: '9px', fontFamily: 'Georgia, serif', color: '#6b4020',
        wordWrap: { width: cardW - 50 },
      }));

      const priceStr = `${c.price} ${c.currency === 'tokens' ? '◆' : '🪙'}  ·  ${c.durationWaves < 900 ? `${c.durationWaves} waves` : 'One use'}`;
      this.scrollContainer.add(this.add.text(iconX + 28, y + 56, priceStr, {
        fontSize: '9px', fontFamily: 'Georgia, serif', color: '#4a3020',
      }));
    }

    const rows = Math.ceil(CONSUMABLE_DEFS.length / cols);
    const contentH = 32 + rows * (CARD_H + 6) + 10;
    const SCROLL_AREA_H = 576 - 82;
    this.maxScroll = Math.max(0, contentH - SCROLL_AREA_H);
  }

  // ── Mimic Trophies tab ──
  private buildMimicContent(W: number): void {
    const cols = 3;
    const PAD = 10;
    const GAP = 8;
    const cardW = Math.floor((W - PAD * 2 - GAP * (cols - 1)) / cols);
    const CARD_H = 70;
    let y = 10;

    const hdr = this.add.text(W / 2, y + 6, '✦  Mimic Trophies  ✦', {
      fontSize: '11px', fontFamily: 'Georgia, serif', color: '#a07840', fontStyle: 'bold',
    }).setOrigin(0.5);
    this.scrollContainer.add(hdr);

    const subHdr = this.add.text(W / 2, y + 24, 'Kill a Mimic (🪙) or defeat a Goblino to earn trophies — they go in the Reserved slot', {
      fontSize: '9px', fontFamily: 'Georgia, serif', color: '#5a3600',
    }).setOrigin(0.5);
    this.scrollContainer.add(subHdr);
    y += 42;

    const RARITY_COLORS: Record<string, string> = { epic: '#a855f7', legendary: '#f97316', unique: '#cc3322' };

    for (let i = 0; i < TROPHY_POOL.length; i++) {
      const trophy = TROPHY_POOL[i];
      const col = i % cols;
      const row = Math.floor(i / cols);
      if (col === 0) y = 52 + row * (CARD_H + 6);

      const cx = PAD + col * (cardW + GAP) + cardW / 2;
      const cy = y + CARD_H / 2;
      const rarColor = Phaser.Display.Color.HexStringToColor(RARITY_COLORS[trophy.rarity] ?? '#cc3322').color;

      const bg = this.add.rectangle(cx, cy, cardW, CARD_H - 4, 0x0d0a08, 0.95)
        .setStrokeStyle(1, rarColor, trophy.rarity === 'legendary' ? 0.8 : 0.6);
      this.scrollContainer.add(bg);

      const iconX = PAD + col * (cardW + GAP) + 13;

      // Trophy sprite icon
      if (trophy.iconKey && this.textures.exists(trophy.iconKey)) {
        const iconImg = this.add.image(iconX + 8, cy, trophy.iconKey).setDisplaySize(22, 22);
        this.scrollContainer.add(iconImg);
      }

      // Text offset: shift right if icon is shown
      const hasIcon = !!(trophy.iconKey && this.textures.exists(trophy.iconKey));
      const textX = hasIcon ? iconX + 26 : iconX;
      const textW = hasIcon ? cardW - 46 : cardW - 20;

      // Rarity badge
      const rarBadge = this.add.text(textX, y + 6, trophy.rarity === 'legendary' ? 'LEGENDARY' : 'EPIC', {
        fontSize: '9px', fontFamily: 'Georgia, serif',
        color: RARITY_COLORS[trophy.rarity] ?? '#cc3322',
        backgroundColor: '#0a0608', padding: { x: 3, y: 1 },
      });
      this.scrollContainer.add(rarBadge);

      this.scrollContainer.add(this.add.text(textX, y + 20, trophy.name, {
        fontSize: '10px', fontFamily: 'Georgia, serif',
        color: RARITY_COLORS[trophy.rarity] ?? '#cc3322', fontStyle: 'bold',
        wordWrap: { width: textW },
      }));

      this.scrollContainer.add(this.add.text(textX, y + 36, trophy.effect, {
        fontSize: '9px', fontFamily: 'Georgia, serif', color: '#6b4020',
        wordWrap: { width: textW },
      }));
    }

    const rows = Math.ceil(TROPHY_POOL.length / cols);
    const contentH = 52 + rows * (CARD_H + 6) + 10;
    const SCROLL_AREA_H = 576 - 82;
    this.maxScroll = Math.max(0, contentH - SCROLL_AREA_H);
  }

  private doScroll(delta: number, headerH: number, scrollAreaH: number): void {
    this.scrollY = Phaser.Math.Clamp(this.scrollY + delta, 0, this.maxScroll);
    this.scrollContainer.y = headerH - this.scrollY;
    if (this.scrollBarThumb && this.maxScroll > 0) {
      const pct = this.scrollY / this.maxScroll;
      const trackRange = scrollAreaH - this.scrollBarThumb.height;
      this.scrollBarThumb.y = headerH + pct * trackRange;
    }
  }
}
